import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HypervisorType, ProductType } from "./enums";
import * as moment from "moment";
import { JwtHelperService } from "@auth0/angular-jwt";
import { EMAIL_REGEX, HOSTNAME_REGEX, IP_REGEX, SEARCHDOMAIN_REGEX, SUBNET_REGEX } from "./constants";
import { BrowserStorage } from "./storage";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class CristieUtils {
  constructor(private router: Router) {}

  static getJSON(obj: any): string {
    return JSON.stringify(obj);
  }

  static async sleep(seconds: number) {
    await new Promise((resolve) => setTimeout(() => resolve(null), seconds * 1000)).then();
  }

  isState(state: string) {
    const routeState = state.startsWith("/") ? state : `/${state}`;
    //If the url has query params then remove them first
    return this.router.url.split("?")[0].startsWith(routeState);
  }

  static enumFromValue<T>(e: T, value: unknown): T[keyof T] | null {
    for (const [k, v] of Object.entries(e)) {
      if (typeof value === "number") value = value.toString();
      if (v == value) return e[k as keyof T];
    }
    return null;
  }

  static splitToEnumArray(value: string | string[], e: any): any[] {
    const enumList = [];
    if (value instanceof String || typeof value === "string") {
      const list = value.split(",");
      for (const l of list) {
        enumList.push(CristieUtils.enumFromValue(e, l));
      }
    }

    if (value instanceof Array) {
      for (const v of value) {
        enumList.push(CristieUtils.enumFromValue(e, v));
      }
    }

    return enumList;
  }

  static cloneObject(obj) {
    return Object.assign({}, obj);
  }

  static cloneArrayOfObjects(array: any[]) {
    return array.map((a) => Object.assign({}, a));
  }

  static dateToMoment(date: Date) {
    // GUI needs moment, backend needs Date. Cast it as unknown then as moment.
    return date as unknown as moment.Moment;
  }

  static momentToDate(moment: moment.Moment) {
    // The same as above but opposite
    return moment.toDate();
  }

  static hypervisorTypeToString(type: HypervisorType): string {
    switch (type) {
      case HypervisorType.AMAZON:
        return "Amazon EC2";
      case HypervisorType.AZURE:
        return "Microsoft Azure";
      case HypervisorType.HMC:
        return "IBM HMC";
      case HypervisorType.HYPERV:
        return "Microsoft Hyper-V";
      case HypervisorType.NUTANIX:
        return "Nutanix AHV";
      case HypervisorType.ORACLE:
        return "Oracle Cloud";
      case HypervisorType.VSPHERE:
        return "VMware vSphere";
      default:
        throw new Error("Unknown task type");
    }
  }

  static enumFromKey(e: any, value: any) {
    for (const [k] of Object.entries(e)) {
      if (typeof value == "number") value = value.toString();
      if (k == value) return e[k];
    }
    return null;
  }

  /**
   * Checks if a string is null, empty or undefined.
   * @param value The string you want to check
   * @returns boolean
   */
  static isNullOrEmpty(value: string): boolean {
    if (value == "" || value == null || value == undefined) return true;
    return false;
  }

  static encodeBase64(value: string): string {
    const bytes = new TextEncoder().encode(value || "");
    let binary = "";
    bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
    return btoa(binary);
  }

  static decodeBase64(value: string): string {
    if (value == null) return "";

    try {
      const binary = atob(value);
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      const decoded = new TextDecoder().decode(bytes);
      return decoded.includes("\uFFFD") ? binary : decoded;
    } catch {
      try {
        return atob(value);
      } catch {
        return "";
      }
    }
  }

  static validateIPV4(ipAddr: string) {
    return new RegExp(IP_REGEX).test(ipAddr);
  }

  static validateNetmask(mask: string) {
    return new RegExp(SUBNET_REGEX).test(mask);
  }

  static validateHostname(hostname: string) {
    return new RegExp(HOSTNAME_REGEX).test(hostname);
  }

  static validateSearchDomain(searchdomain: string) {
    return new RegExp(SEARCHDOMAIN_REGEX).test(searchdomain);
  }

  static capitalise(s: string): string {
    const c = s.toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
    return c;
  }

  static formatMAC(m: string) {
    return m.toLowerCase().replace(/-/g, ":");
  }

  static validateEmail(email: string) {
    return new RegExp(EMAIL_REGEX).test(email);
  }

  static removeFromArray(item: any, array: any[]): boolean {
    const idx = array.indexOf(item);
    if (array.indexOf(item) > -1) {
      array.splice(idx, 1);
      return true;
    } else {
      return false;
    }
  }

  static getWinPEVersion(product: ProductType, os: string): string {
    os = os.toLowerCase();
    if (os.includes("2016") || os.includes("2019") || os.includes("2022") || os.includes("windows 10") || os.includes("windows 11")) {
      if (product == ProductType.CLONEMANAGER) return "pe10";
      return "pe11";
    }
    return "pe5";
  }

  static getUserFromToken(): number {
    const jwtHelper = new JwtHelperService();
    const token = BrowserStorage.getString("id_token", true);
    if (token == null) return null;
    const decoded = jwtHelper.decodeToken(token);
    return decoded.identity;
  }

  /**
   * Checks if 2 objects are identical or have any changes
   * @param a The object you want to check
   *  * @param b The other object you want to check
   * @returns boolean
   */
  static hasChanges(a, b) {
    const aProps = Object.getOwnPropertyNames(a);
    const bProps = Object.getOwnPropertyNames(b);
    if (aProps.length != bProps.length) {
      return true;
    }
    for (const propName of aProps) {
      if (a[propName] !== b[propName]) {
        return true;
      }
    }
    return false;
  }

  static isDefined(value: any): boolean {
    return typeof value !== "undefined";
  }

  /**
   * Determines the current browser verison & if localstorage is enabled
   * @returns @Browser object
   */
  static getBrowser(): Browser {
    // browser
    const nAgt = navigator.userAgent;
    let browser = navigator.appName;
    let version = "" + parseFloat(navigator.appVersion);
    let majorVersion = parseInt(navigator.appVersion, 10);

    let nameOffset, verOffset, ix;

    if ((verOffset = nAgt.indexOf("Opera")) !== -1) {
      browser = "Opera";
      version = nAgt.substring(verOffset + 6);
      if ((verOffset = nAgt.indexOf("Version")) !== -1) {
        version = nAgt.substring(verOffset + 8);
      }
    }
    if ((verOffset = nAgt.indexOf("OPR")) !== -1) {
      // Opera next
      browser = "Opera";
      version = nAgt.substring(verOffset + 4);
    } else if ((verOffset = nAgt.indexOf("MSIE")) !== -1) {
      // IE
      browser = "Microsoft Internet Explorer";
      version = nAgt.substring(verOffset + 5);
    } else if ((verOffset = nAgt.indexOf("Edge")) !== -1) {
      browser = "Edge";
      version = nAgt.substring(verOffset + 5);
    } else if ((verOffset = nAgt.indexOf("Chrome")) !== -1) {
      browser = "Chrome";
      version = nAgt.substring(verOffset + 7);
    } else if ((verOffset = nAgt.indexOf("Safari")) !== -1) {
      browser = "Safari";
      version = nAgt.substring(verOffset + 7);
      if ((verOffset = nAgt.indexOf("Version")) !== -1) {
        version = nAgt.substring(verOffset + 8);
      }
    } else if ((verOffset = nAgt.indexOf("Firefox")) !== -1) {
      browser = "Firefox";
      version = nAgt.substring(verOffset + 8);
    } else if (nAgt.indexOf("Trident/") !== -1) {
      // IE 11+
      browser = "Microsoft Internet Explorer";
      version = nAgt.substring(nAgt.indexOf("rv:") + 3);
    } else if ((nameOffset = nAgt.lastIndexOf(" ") + 1) < (verOffset = nAgt.lastIndexOf("/"))) {
      // Other browsers
      browser = nAgt.substring(nameOffset, verOffset);
      version = nAgt.substring(verOffset + 1);
      if (browser.toLowerCase() === browser.toUpperCase()) {
        browser = navigator.appName;
      }
    }

    // trim the version string
    if ((ix = version.indexOf(";")) !== -1) version = version.substring(0, ix);
    if ((ix = version.indexOf(" ")) !== -1) version = version.substring(0, ix);
    if ((ix = version.indexOf(")")) !== -1) version = version.substring(0, ix);

    majorVersion = parseInt("" + version, 10);
    if (isNaN(majorVersion)) {
      version = "" + parseFloat(navigator.appVersion);
      majorVersion = parseInt(navigator.appVersion, 10);
    }

    let storageEnabled: boolean;
    try {
      if (typeof localStorage !== "undefined") {
        try {
          BrowserStorage.setString("test", "test");
          BrowserStorage.delete("test");
          storageEnabled = true;
        } catch {
          storageEnabled = false;
        }
      } else {
        storageEnabled = false;
      }
    } catch {
      storageEnabled = false;
    }

    return new Browser(browser, majorVersion, storageEnabled);
  }

  static clickElement(elementId: string) {
    document.getElementById(elementId).click();
  }

  static getFileContents(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = function () {
        resolve(reader.result as string);
      };
      reader.onerror = function () {
        reject(new Error("Failed to read file"));
      };
      reader.readAsText(file);
    });
  }

  static isNullOrUndefined(v) {
    if (v == undefined || v == null) return true;
    return false;
  }

  static isUndefined(v) {
    return v == undefined;
  }

  static isString(v) {
    return typeof v === "string";
  }

  static getProductTypeLabel(pt: ProductType): string {
    switch (pt) {
      case ProductType.COBMR:
        return "CoBMR";
      case ProductType.CLONEMANAGER:
        return "CloneManager Online";
      case ProductType.CMLITE:
        return "CloneManager Offline";
      default:
        return pt;
    }
  }

  static isLocalhost(): boolean {
    if (!environment.production) return false;
    const hostname = window.location.hostname;
    return hostname === "127.0.0.1" || hostname === "localhost";
  }
} // End

export class Browser {
  name: string;
  version: number;
  storageEnabled: boolean;

  constructor(name: string, version: number, storageEnabled: boolean) {
    this.name = name;
    this.version = version;
    this.storageEnabled = storageEnabled;
  }
}
